// client/src/routes/app/equipment/new.tsx — adding an installation to the register · MAINT-006
// (04 §8.9's LIST → FORM pair).
//
// 04 §8.9 gives SCR-EQP the two screens the module needs and no third one for the create, the
// way §8.9 does for repairs: a new equipment is the register's primary action and lands back on
// the detail it just made. That is what this route is — no new canonical screen, one form on its
// own URL so the browser's Back means „I changed my mind" instead of losing what was typed.
//
// `Operations.Write`, not `Operations.Read`: a membership that may read the register must not
// reach this URL by typing it (the split BANK-004 recorded as D-725).
//
// `?propertyId=` is where „Echipament nou" on a property's profile sends the form (D-2840): the
// apartment is already chosen, and „Renunțați" and the way back return to that profile's tab.

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { EquipmentForm } from '@/features/equipment/EquipmentForm';
import { useEquipmentErrorMessage } from '@/features/equipment/errors';
import { useCreateEquipment } from '@/features/equipment/queries';
import { toEquipmentInput, type EquipmentFormValues } from '@/features/equipment/schema';
import { toast } from '@/stores/toast-store';

const newEquipmentSearchSchema = z.object({
  propertyId: z.string().uuid().optional(),
});

export const Route = createFileRoute('/app/equipment/new')({
  validateSearch: newEquipmentSearchSchema,
  component: NewEquipmentScreen,
});

function NewEquipmentScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { propertyId } = Route.useSearch();
  const create = useCreateEquipment();
  const describe = useEquipmentErrorMessage();
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (values: EquipmentFormValues) => {
    setFailure(null);
    try {
      const detail = await create.mutateAsync({ body: toEquipmentInput(values) });
      toast.success(t('equipment.form.created'));
      await navigate({ to: '/app/equipment/$equipmentId', params: { equipmentId: detail.id } });
    } catch (error) {
      setFailure(describe(error));
    }
  };

  return (
    <RequirePermission permission="Operations.Write">
      <PageHeader
        title={t('equipment.form.newTitle')}
        description={t('equipment.form.newSubtitle')}
        backTo={propertyId === undefined ? '/app/equipment' : '/app/properties/$propertyId/equipment'}
        backParams={propertyId === undefined ? undefined : { propertyId }}
        backLabel={
          propertyId === undefined
            ? t('equipment.detail.backToRegister')
            : t('properties.equipment.title')
        }
      />
      <EquipmentForm
        defaultPropertyId={propertyId}
        submitting={create.isPending}
        failure={failure}
        onSubmit={(values) => void submit(values)}
        onCancel={() => {
          if (propertyId === undefined) {
            void navigate({ to: '/app/equipment' });
          } else {
            void navigate({
              to: '/app/properties/$propertyId/equipment',
              params: { propertyId },
            });
          }
        }}
      />
    </RequirePermission>
  );
}
